import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Button, Chip,
  CircularProgress, Alert, Checkbox, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, LinearProgress, Stack, FormControl, InputLabel, Select, MenuItem,
  Snackbar
} from '@mui/material';
import {
  Translate as TranslateIcon,
  VolumeUp as AudioIcon,
  Publish as PublishIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  PlayArrow as PlayIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  RecordVoiceOver as VoiceIcon
} from '@mui/icons-material';
import apiClient from '../../services/apiClient';
import { useSettings } from '../../context/SettingsContext';
import StoryPathNav from '../../components/admin/StoryPathNav';

const allLanguages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', flag: '🇰🇷' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' }
];

const langName = (code) => allLanguages.find(l => l.code === code)?.name || code;
// A translation has audio if either flag is present (backend exposes audio_job_id;
// some payloads also set audio_generated).
const hasAudioFor = (t) => !!(t && (t.audio_generated || t.audio_job_id || t.audio_file));
const audioRefFor = (t) => t?.audio_file || t?.audio_job_id || null;

const StoryProductionCenter = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [generating, setGenerating] = useState({});        // per-language translation
  const [generatingAudio, setGeneratingAudio] = useState({}); // per-language audio
  const [publishing, setPublishing] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTranslation, setEditingTranslation] = useState(null);
  const [editFormData, setEditFormData] = useState({ translated_title: '', content: '', translation: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);

  const { ttsVoiceGender: defaultGender, ttsVoiceQualityBreadcrumbs: defaultQuality } = useSettings();
  const [voiceGender, setVoiceGender] = useState(defaultGender || 'Female');
  const [voiceQuality, setVoiceQuality] = useState(defaultQuality || 'medium');

  const [asyncJob, setAsyncJob] = useState(null); // {status, progress_percentage, completed_languages, failed_languages, total_languages}
  const pollIntervalRef = useRef(null);

  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setNotification({ open: true, message, severity });

  useEffect(() => {
    fetchStory();
    return () => { if (pollIntervalRef.current) clearInterval(pollIntervalRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const fetchStory = async () => {
    setLoading(true);
    try {
      const response = await apiClient.admin.getStory(storyId);
      setStory(response.data.story);
      const existingLangs = response.data.story.translations?.map(t => t.language_code) || [];
      setSelectedLanguages(allLanguages.map(l => l.code).filter(code => !existingLangs.includes(code)));
    } catch (error) {
      console.error('Error fetching story:', error);
      notify('Failed to load story', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageToggle = (languageCode) => {
    setSelectedLanguages(prev =>
      prev.includes(languageCode) ? prev.filter(l => l !== languageCode) : [...prev, languageCode]
    );
  };

  // --- Translation ---
  const generateTranslationForLanguage = async (languageCode) => {
    setGenerating(prev => ({ ...prev, [languageCode]: true }));
    try {
      await apiClient.admin.generateTranslations(storyId, [languageCode]);
      await fetchStory();
      notify(`Translation generated for ${langName(languageCode)}`);
    } catch (error) {
      notify(error.response?.data?.error || `Failed to translate ${langName(languageCode)}`, 'error');
    } finally {
      setGenerating(prev => ({ ...prev, [languageCode]: false }));
    }
  };

  const generateSelectedTranslations = async () => {
    if (selectedLanguages.length === 0) {
      notify('Select at least one language to translate', 'warning');
      return;
    }
    const all = {};
    selectedLanguages.forEach(lang => { all[lang] = true; });
    setGenerating(all);

    try {
      const response = await apiClient.admin.asyncTranslation.start(storyId, selectedLanguages, false);
      const jobData = response.data;
      setAsyncJob({
        status: 'initiated',
        total_languages: jobData.total_languages,
        progress_percentage: 0,
        completed_languages: [],
        failed_languages: []
      });
      pollIntervalRef.current = setInterval(() => pollAsyncJobStatus(jobData.job_id), 2000);
      notify(`Translating ${selectedLanguages.length} language(s) in the background…`, 'info');
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to start translation job', 'error');
      setGenerating({});
    }
  };

  const pollAsyncJobStatus = async (jobId) => {
    try {
      const response = await apiClient.admin.asyncTranslation.getStatus(jobId);
      const status = response.data;
      setAsyncJob(status);

      if (status.status === 'completed' || status.status === 'partial' || status.status === 'failed') {
        clearInterval(pollIntervalRef.current);
        setGenerating({});
        await fetchStory();

        if (status.status === 'completed') {
          notify(`All ${status.total_languages} translations generated`, 'success');
          setSelectedLanguages([]);
        } else if (status.status === 'partial') {
          notify(`Partly done — failed: ${(status.failed_languages || []).join(', ') || 'some'}`, 'warning');
          setSelectedLanguages(prev => prev.filter(l => !(status.completed_languages || []).includes(l)));
        } else {
          notify('Translation job failed', 'error');
        }
        // Clear the banner shortly after completion
        setTimeout(() => setAsyncJob(null), 2500);
      }
    } catch (error) {
      clearInterval(pollIntervalRef.current);
      setGenerating({});
      setAsyncJob(null);
      notify(error.response?.data?.error || 'Error checking translation status', 'error');
    }
  };

  // --- Audio ---
  const generateAudioForLanguage = async (languageCode) => {
    setGeneratingAudio(prev => ({ ...prev, [languageCode]: true }));
    try {
      await apiClient.admin.generateAudio(storyId, [languageCode], {
        voice_gender: voiceGender, voice_quality: voiceQuality
      });
      await fetchStory();
      notify(`Audio generated for ${langName(languageCode)}`);
    } catch (error) {
      notify(error.response?.data?.error || `Failed to generate audio for ${langName(languageCode)}`, 'error');
    } finally {
      setGeneratingAudio(prev => ({ ...prev, [languageCode]: false }));
    }
  };

  const generateAllMissingAudio = async () => {
    const languagesToGenerate = (story?.translations || [])
      .filter(t => !hasAudioFor(t))
      .map(t => t.language_code);
    if (languagesToGenerate.length === 0) {
      notify('All translations already have audio', 'info');
      return;
    }
    const all = {};
    languagesToGenerate.forEach(lang => { all[lang] = true; });
    setGeneratingAudio(all);
    try {
      await apiClient.admin.generateAudio(storyId, languagesToGenerate, {
        voice_gender: voiceGender, voice_quality: voiceQuality
      });
      await fetchStory();
      notify('Audio generated for all missing languages');
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to generate audio', 'error');
    } finally {
      setGeneratingAudio({});
    }
  };

  // --- Translation edit / delete / preview ---
  const handleEditTranslation = (translation) => {
    setEditingTranslation(translation);
    setEditFormData({
      translated_title: translation.translated_title || '',
      content: translation.content || '',
      translation: translation.translation || ''
    });
    setEditDialogOpen(true);
  };

  const handleSaveTranslation = async () => {
    setSavingEdit(true);
    try {
      await apiClient.admin.updateTranslation(storyId, editingTranslation.language_code, editFormData);
      await fetchStory();
      setEditDialogOpen(false);
      notify('Translation updated');
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to update translation', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const deleteTranslation = async (languageCode) => {
    if (!window.confirm(`Delete the ${langName(languageCode)} translation?`)) return;
    try {
      await apiClient.admin.deleteTranslation(storyId, languageCode);
      await fetchStory();
      notify(`${langName(languageCode)} translation deleted`);
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to delete translation', 'error');
    }
  };

  const handlePreview = (translation) => {
    setPreviewContent(translation);
    setPreviewDialogOpen(true);
  };

  const playAudioSample = (translation) => {
    const ref = audioRefFor(translation);
    if (!ref) return;
    window.open(apiClient.audio.getAudioUrl(ref), '_blank');
  };

  const publishStory = async () => {
    setPublishing(true);
    try {
      await apiClient.admin.publishStory(storyId);
      await fetchStory();
      notify('Story published');
    } catch (error) {
      notify(error.response?.data?.error || 'Failed to publish story', 'error');
    } finally {
      setPublishing(false);
    }
  };

  // --- Derived ---
  const translations = story?.translations || [];
  const stats = {
    totalLanguages: allLanguages.length,
    translationsComplete: translations.length,
    audioGenerated: translations.filter(hasAudioFor).length,
    readyToPublish: translations.length > 0 && translations.every(hasAudioFor)
  };
  const missingAudioCount = translations.filter(t => !hasAudioFor(t)).length;
  const anyTranslating = Object.values(generating).some(Boolean) || !!asyncJob;
  const anyAudio = Object.values(generatingAudio).some(Boolean);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, pb: 10 }}>
      {/* Jump between stories in this path without leaving the editor */}
      <StoryPathNav buildTo={(id) => `/admin/stories/${id}/translations`} />

      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'flex-start' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <IconButton onClick={() => navigate(`/admin/stories/${storyId}`)} aria-label="Back to story" sx={{ mt: 0.25 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>{story?.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              Story production · translations, audio &amp; publishing
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={story?.is_published ? <CheckCircleIcon /> : <UncheckedIcon />}
            label={story?.is_published ? 'Published' : 'Draft'}
            color={story?.is_published ? 'success' : 'default'}
            variant="outlined"
            size="small"
          />
          <Box sx={{ textAlign: 'right' }}>
            <Button
              variant="contained"
              color="success"
              onClick={publishStory}
              disabled={publishing || story?.is_published || !stats.readyToPublish}
              startIcon={publishing ? <CircularProgress size={20} /> : <PublishIcon />}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {publishing ? 'Publishing…' : story?.is_published ? 'Published' : 'Publish story'}
            </Button>
            {!story?.is_published && !stats.readyToPublish && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Generate all translations &amp; audio first
              </Typography>
            )}
          </Box>
        </Stack>
      </Stack>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Translations</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                {stats.translationsComplete}/{stats.totalLanguages}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(stats.translationsComplete / stats.totalLanguages) * 100}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Audio ready</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
                {stats.audioGenerated}/{stats.translationsComplete || 0}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              color="info"
              value={stats.translationsComplete > 0 ? (stats.audioGenerated / stats.translationsComplete) * 100 : 0}
              sx={{ height: 8, borderRadius: 1 }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {stats.readyToPublish ? 'Ready to publish.' : 'Finish translations and audio to publish.'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Voice settings — always visible */}
      <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <VoiceIcon color="action" />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Voice settings for audio</Typography>
              <Typography variant="caption" color="text.secondary">
                Applied to all audio generation (individual and batch).
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Gender</InputLabel>
              <Select value={voiceGender} label="Gender" onChange={(e) => setVoiceGender(e.target.value)}>
                <MenuItem value="Female">Female</MenuItem>
                <MenuItem value="Male">Male</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Quality</InputLabel>
              <Select value={voiceQuality} label="Quality" onChange={(e) => setVoiceQuality(e.target.value)}>
                <MenuItem value="low">Standard</MenuItem>
                <MenuItem value="medium">Neural</MenuItem>
                <MenuItem value="high">Studio</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      {/* Batch actions */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2, alignItems: { sm: 'center' } }}>
        <Button
          variant="contained"
          startIcon={anyTranslating ? <CircularProgress size={18} /> : <TranslateIcon />}
          onClick={generateSelectedTranslations}
          disabled={anyTranslating || selectedLanguages.length === 0}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Generate {selectedLanguages.length || ''} selected translation{selectedLanguages.length === 1 ? '' : 's'}
        </Button>
        <Button
          variant="outlined"
          startIcon={anyAudio ? <CircularProgress size={18} /> : <AudioIcon />}
          onClick={generateAllMissingAudio}
          disabled={anyAudio || missingAudioCount === 0}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Generate all missing audio{missingAudioCount ? ` (${missingAudioCount})` : ''}
        </Button>
      </Stack>

      {/* Async translation progress banner */}
      {asyncJob && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Translating… {asyncJob.progress_percentage != null ? `${Math.round(asyncJob.progress_percentage)}%` : ''}
            {asyncJob.failed_languages?.length ? ` · failed: ${asyncJob.failed_languages.join(', ')}` : ''}
          </Typography>
          <LinearProgress
            variant={asyncJob.progress_percentage != null ? 'determinate' : 'indeterminate'}
            value={asyncJob.progress_percentage || 0}
            sx={{ height: 6, borderRadius: 1 }}
          />
        </Paper>
      )}

      {/* Languages table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell sx={{ fontWeight: 700 }}>Language</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Translation</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Audio</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {allLanguages.map(language => {
              const translation = translations.find(t => t.language_code === language.code);
              const hasTranslation = !!translation;
              const hasAudio = hasAudioFor(translation);
              const isGenerating = generating[language.code];
              const isGeneratingAudio = generatingAudio[language.code];

              return (
                <TableRow key={language.code} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={hasTranslation || selectedLanguages.includes(language.code)}
                      disabled={hasTranslation || anyTranslating}
                      onChange={() => handleLanguageToggle(language.code)}
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <span style={{ fontSize: '1.2rem' }}>{language.flag}</span>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{language.name}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    {hasTranslation
                      ? <CheckCircleIcon color="success" fontSize="small" />
                      : (isGenerating ? <CircularProgress size={16} /> : <UncheckedIcon color="disabled" fontSize="small" />)}
                  </TableCell>
                  <TableCell align="center">
                    {hasAudio
                      ? <AudioIcon color="info" fontSize="small" />
                      : (isGeneratingAudio ? <CircularProgress size={16} /> : <UncheckedIcon color="disabled" fontSize="small" />)}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" color={translation?.translated_title ? 'text.primary' : 'text.secondary'} noWrap>
                      {translation?.translated_title || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {!hasTranslation ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={isGenerating ? <CircularProgress size={16} /> : <TranslateIcon />}
                        onClick={() => generateTranslationForLanguage(language.code)}
                        disabled={isGenerating || anyTranslating}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                      >
                        Translate
                      </Button>
                    ) : (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Preview">
                          <IconButton size="small" onClick={() => handlePreview(translation)}><PreviewIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleEditTranslation(translation)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        {hasAudio ? (
                          <Tooltip title="Play audio">
                            <IconButton size="small" color="info" onClick={() => playAudioSample(translation)}><PlayIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Generate audio">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => generateAudioForLanguage(language.code)}
                                disabled={isGeneratingAudio}
                              >
                                {isGeneratingAudio ? <CircularProgress size={16} /> : <AudioIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete translation">
                          <IconButton size="small" color="error" onClick={() => deleteTranslation(language.code)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !savingEdit && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Edit translation — {editingTranslation && langName(editingTranslation.language_code)}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            label="Translated Title" fullWidth sx={{ mb: 2, mt: 1 }}
            value={editFormData.translated_title}
            onChange={(e) => setEditFormData({ ...editFormData, translated_title: e.target.value })}
          />
          <TextField
            label="Content (target language)" fullWidth multiline rows={6} sx={{ mb: 2 }}
            value={editFormData.content}
            onChange={(e) => setEditFormData({ ...editFormData, content: e.target.value })}
          />
          <TextField
            label="English translation" fullWidth multiline rows={6}
            value={editFormData.translation}
            onChange={(e) => setEditFormData({ ...editFormData, translation: e.target.value })}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={savingEdit} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSaveTranslation} variant="contained" disabled={savingEdit}
            startIcon={savingEdit ? <CircularProgress size={18} /> : null}
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            {savingEdit ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Preview — {previewContent && langName(previewContent.language_code)}
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="h6" sx={{ fontWeight: 700 }} gutterBottom>{previewContent?.translated_title}</Typography>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Content</Typography>
          <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2, whiteSpace: 'pre-wrap' }}>
            <Typography variant="body2">{previewContent?.content}</Typography>
          </Paper>
          <Typography variant="subtitle2" gutterBottom>English translation</Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, whiteSpace: 'pre-wrap' }}>
            <Typography variant="body2">{previewContent?.translation}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPreviewDialogOpen(false)} sx={{ textTransform: 'none', borderRadius: 2 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          elevation={6}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StoryProductionCenter;
